import { useRef, useEffect } from "react";

// ── Configurable parameters ─────────────────────────────
const CONFIG = {
  brushRadius: 140,
  brushStrength: 14,
  brushSoftness: 2.4,
  inertia: 0.07,
  trailDecay: 0.93,
  ambientSpeed: 0.12,
  ambientAmount: 1.6,
  localGridSize: 6,
};

function smoothstep(edge0, edge1, x) {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

export const InteractiveInk = ({ src }) => {
  const canvasRef = useRef(null);
  const stateRef = useRef({
    mouse: { x: -9999, y: -9999 },
    smooth: { x: -9999, y: -9999 },
    prev: { x: -9999, y: -9999 },
    img: null,
    raf: null,
    time: 0,
    // Persistent trail field for the brush area
    trailMap: new Map(),
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const s = stateRef.current;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      s.mouse.x = (e.clientX - rect.left) * (canvas.width / rect.width);
      s.mouse.y = (e.clientY - rect.top) * (canvas.height / rect.height);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("resize", resize);

    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      s.img = img;
      resize();
      loop();
    };
    img.src = src;

    function loop() {
      if (!s.img || !canvasRef.current) return;

      const w = canvas.width;
      const h = canvas.height;
      const t = s.time;
      const image = s.img;
      const {
        brushRadius, brushStrength, brushSoftness,
        inertia, trailDecay, ambientSpeed, ambientAmount, localGridSize,
      } = CONFIG;

      // ── Inertia-smoothed mouse ────────────────────
      s.smooth.x += (s.mouse.x - s.smooth.x) * inertia;
      s.smooth.y += (s.mouse.y - s.smooth.y) * inertia;

      const velX = s.smooth.x - s.prev.x;
      const velY = s.smooth.y - s.prev.y;
      s.prev.x = s.smooth.x;
      s.prev.y = s.smooth.y;
      const speed = Math.sqrt(velX * velX + velY * velY);
      const pressure = Math.min(1, speed / 6);

      // ── Ambient sway (whole image) ────────────────
      const ambX = Math.sin(t * ambientSpeed) * ambientAmount
                  + Math.cos(t * ambientSpeed * 0.7 + 1.2) * ambientAmount * 0.5;
      const ambY = Math.cos(t * ambientSpeed * 0.8 + 0.5) * ambientAmount * 0.8
                  + Math.sin(t * ambientSpeed * 0.5 + 2.0) * ambientAmount * 0.4;

      // ── PASS 1: Draw full image with ambient sway ─
      // Scale image to cover canvas with padding for sway
      const pad = ambientAmount * 4 + brushStrength + 4;
      const drawW = w + pad * 2;
      const drawH = h + pad * 2;

      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(image, -pad + ambX, -pad + ambY, drawW, drawH);

      // ── PASS 2: Localized brush displacement ──────
      const mx = s.smooth.x;
      const my = s.smooth.y;

      // Only process if mouse is on canvas
      if (mx > -brushRadius && mx < w + brushRadius && my > -brushRadius && my < h + brushRadius) {
        // Define the affected rectangle
        const rx = Math.max(0, Math.floor(mx - brushRadius - brushStrength));
        const ry = Math.max(0, Math.floor(my - brushRadius - brushStrength));
        const rx2 = Math.min(w, Math.ceil(mx + brushRadius + brushStrength));
        const ry2 = Math.min(h, Math.ceil(my + brushRadius + brushStrength));
        const rw = rx2 - rx;
        const rh = ry2 - ry;

        if (rw > 0 && rh > 0) {
          // Subdivide the brush area into small local cells
          const cellSize = localGridSize;
          const cols = Math.ceil(rw / cellSize);
          const rows = Math.ceil(rh / cellSize);

          // Source scaling
          const sx = image.naturalWidth / drawW;
          const sy = image.naturalHeight / drawH;

          for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
              const px = rx + c * cellSize;
              const py = ry + r * cellSize;
              const cw = Math.min(cellSize, rx2 - px);
              const ch = Math.min(cellSize, ry2 - py);

              const centerX = px + cw / 2;
              const centerY = py + ch / 2;

              const dx = centerX - mx;
              const dy = centerY - my;
              const dist = Math.sqrt(dx * dx + dy * dy);

              if (dist >= brushRadius) continue;

              // Smoothstep falloff
              const falloff = 1 - smoothstep(0, brushRadius, dist);
              const soft = Math.pow(falloff, brushSoftness);
              const str = brushStrength * (0.5 + pressure * 0.5);

              // Radial push
              const nd = Math.max(dist, 0.5);
              let ox = (dx / nd) * soft * str;
              let oy = (dy / nd) * soft * str;

              // Trail persistence (paper drag)
              const key = (Math.floor(px / cellSize)) * 10000 + Math.floor(py / cellSize);
              const prev = s.trailMap.get(key) || [0, 0];
              ox = prev[0] * trailDecay + ox * (1 - trailDecay);
              oy = prev[1] * trailDecay + oy * (1 - trailDecay);
              s.trailMap.set(key, [ox, oy]);

              // Skip if displacement is negligible
              if (Math.abs(ox) < 0.3 && Math.abs(oy) < 0.3) continue;

              // Source position (from original image, offset by displacement)
              const imgSrcX = (px + pad - ambX - ox) * sx;
              const imgSrcY = (py + pad - ambY - oy) * sy;
              const imgSrcW = cw * sx;
              const imgSrcH = ch * sy;

              ctx.drawImage(
                image,
                Math.max(0, imgSrcX),
                Math.max(0, imgSrcY),
                Math.min(imgSrcW, image.naturalWidth - imgSrcX),
                Math.min(imgSrcH, image.naturalHeight - imgSrcY),
                px, py, cw, ch
              );
            }
          }
        }
      }

      // Decay orphaned trail entries
      if (s.trailMap.size > 5000) {
        for (const [key, val] of s.trailMap) {
          if (Math.abs(val[0]) < 0.1 && Math.abs(val[1]) < 0.1) {
            s.trailMap.delete(key);
          }
        }
      }

      s.time += 0.016;
      s.raf = requestAnimationFrame(loop);
    }

    return () => {
      if (s.raf) cancelAnimationFrame(s.raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", resize);
    };
  }, [src]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      data-testid="interactive-ink-canvas"
    />
  );
};
